import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  InputNumber,
  Card,
  Row,
  Col,
  notification,
  Upload,
  Descriptions,
  Alert,
  Checkbox,
} from "antd";
import moment from "moment";
import { customBaseUrl, httpGet, httpPost, httpPostFormData } from "../../store/http";
import { showLoader, hideLoader } from "../../utils/loader";
import { NotificationManager } from "react-notifications";
import AdminLayout from "../../components/Layout";
import LoanDashboardLayout from "../../components/Layout/LoanDashboardLayout";

import color from "../../theme/color";
import shadows from "../../theme/shadows";

const { Option } = Select;

const styles = {
  container: {
    padding: "24px",
    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    minHeight: "100vh",
  },
  card: {
    borderRadius: "16px",
    boxShadow: shadows[11],
    border: "none",
    marginBottom: "24px",
    backdropFilter: "blur(10px)",
    background: "rgba(255, 255, 255, 0.9)",
  },
  secondaryCard: {
    borderRadius: "12px",
    boxShadow: shadows[4],
    border: "1px solid #f0f0f0",
    marginBottom: "16px",
    background: "#ffffff",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#333",
    marginBottom: "24px",
    textAlign: "center",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: color.primary,
    marginBottom: "16px",
  },
  input: {
    borderRadius: "8px",
  },
  submitBtn: {
    height: "48px",
    borderRadius: "24px",
    fontSize: "16px",
    fontWeight: "600",
    background: `linear-gradient(90deg, ${color.primary} 0%, #ff7e5f 100%)`,
    border: "none",
    boxShadow: "0 4px 15px rgba(231, 71, 44, 0.3)",
    marginTop: "24px",
    transition: "all 0.3s ease",
  },
  fetchStatus: {
    marginTop: "8px",
    padding: "12px",
    borderRadius: "8px",
    background: "#f0f9ff",
    border: "1px solid #bae7ff",
  }
};

const ApplyForLoan = ({ from }) => {
  const [form] = Form.useForm();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [repaymentAccountDetails, setRepaymentAccountDetails] = useState(null);
  const [tierOrderLevel, setTierOrderLevel] = useState(0);
  const [isTierBlocked, setIsTierBlocked] = useState(false);
  const [fetchedBvn, setFetchedBvn] = useState("");
  const [guarantorStatuses, setGuarantorStatuses] = useState([]); // Array to track each guarantor's validity
  const [statementOfAccountFile, setStatementOfAccountFile] = useState(null);
  const [uploadedStatementOfAccountUrl, setUploadedStatementOfAccountUrl] = useState(null);
  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      notification.error({ message: 'You can only upload JPG/PNG file!' });
    }
    const isLt100K = file.size / 1024 < 100;
    if (!isLt100K) {
      notification.error({ message: 'Image must smaller than 100KB!' });
    }
    return isJpgOrPng && isLt100K;
  };

  const handleChange = async ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  useEffect(() => {
    const fetchBanks = async () => {
      showLoader();
      try {
        const token =
          from === "admin"
            ? localStorage.getItem("token")
            : localStorage.getItem("loanAdminToken");
        const res = await httpGet(
          `${customBaseUrl.loanUrl}/api/v1/admin/actions/banks`,
          null,
          false,
          false,
          token
        );
        if (res.status) {
          setBanks(res.data?.data?.banks);
        } else {
          NotificationManager.error("Failed to fetch banks.");
        }
      } catch (error) {
        NotificationManager.error("Error fetching banks.");
      } finally {
        hideLoader();
      }
    };
    fetchBanks();
  }, [from]);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64Content = reader.result;
        const base64Data = base64Content.split(',')[1] || base64Content;
        resolve(base64Data);
      };
      reader.onerror = (error) => reject(error);
    });

  const handleLoanFileUpload = async (file, documentType, folderName) => {
    if (!file) return null;

    const formData = new FormData();
    formData.append('file', file);

    showLoader();
    try {
      const username = form.getFieldValue('phone') || 'loan_admin_user'; // Use phone as username for now

      const uploadRes = await httpPostFormData(
        `${customBaseUrl.authUrl}/files/upload?documentType=${documentType}&username=${username}&folderName=${folderName}`,
        formData,
        true // Set to true for multipart/form-data
      );

      if (uploadRes.success) {
        NotificationManager.success(`${documentType} uploaded successfully!`);
        return uploadRes.data.fileUrl;
      } else {
        NotificationManager.error(uploadRes.message || `${documentType} upload failed.`);
        return null;
      }
    } catch (error) {
      NotificationManager.error(`Error uploading ${documentType}.`);
      console.error(`${documentType} upload error:`, error);
      return null;
    } finally {
      hideLoader();
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    showLoader();

    const { signatureFiles, accountStatementFile, ...restValues } = values; // Destructure accountStatementFile
    const guarantorDetails = restValues.guarantor_details || [];
    const accountDetails = restValues.account_details || [];

    let statementUrl = uploadedStatementOfAccountUrl;
    if (statementOfAccountFile && !uploadedStatementOfAccountUrl) {
      statementUrl = await handleLoanFileUpload(statementOfAccountFile, 'ACCOUNT_STATEMENT', 'loan-documents');
      if (!statementUrl) {
        setLoading(false);
        hideLoader();
        return;
      }
    } else if (!uploadedStatementOfAccountUrl) {
      NotificationManager.error('Please upload an account statement file.');
      setLoading(false);
      hideLoader();
      return;
    }

    const payload = {
      ...restValues,
      phone: repaymentAccountDetails?.phoneNumber || restValues.phone,
      needed_by: restValues.needed_by
        ? moment(restValues.needed_by).format("YYYY-MM-DD")
        : null,
      loan_start_date: restValues.loan_start_date
        ? moment(restValues.loan_start_date).format("YYYY-MM-DD")
        : null,
      employment_details: (() => {
        const { self_employed, ...details } = restValues.employment_details || {};
        return {
          ...details,
          employment_date: details.employment_date
            ? moment(details.employment_date).format("YYYY-MM-DD")
            : null,
        };
      })(),
      guarantor_details: guarantorDetails.map(({ tier_display_name, ...rest }) => rest),
      account_details: accountDetails,
      signature: signatureFiles && signatureFiles.length > 0 ? await getBase64(signatureFiles[0].originFileObj) : undefined,
      files: {
        ...restValues.files,
        account_statement_url: statementUrl, // Add the uploaded URL here
      },
    };

    try {
      const token =
        from === "admin"
          ? localStorage.getItem("token")
          : localStorage.getItem("loanAdminToken");
      const res = await httpPost(
        `${customBaseUrl.loanUrl}/api/v1/admin/create-loan`,
        payload,
        false,
        token
      );
      if (res.status) {
        NotificationManager.success("Loan application submitted successfully!");
        form.resetFields();
        setStatementOfAccountFile(null); // Clear file state
        setUploadedStatementOfAccountUrl(null); // Clear URL state
        setRepaymentAccountDetails(null);
        setGuarantorStatuses([]);
      } else {
        const errorMsg = res.error || res.message || "Failed to submit loan application.";
        NotificationManager.error(errorMsg);
      }
    } catch (error) {
      NotificationManager.error("Error submitting loan application.");
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const fetchBvnLookup = async (accountNumber) => {
    if (!accountNumber || accountNumber.length !== 10) return null;
    try {
      const token =
        from === "admin"
          ? localStorage.getItem("token")
          : localStorage.getItem("loanAdminToken");
      const res = await httpGet(`${customBaseUrl.loanUrl}/api/v1/loans/waya/bvn-lookup?accountNumber=${accountNumber}`,
        null,
        false,
        false,
        token
      );
      if (res.status && res.data) {
        return res.data; // Assuming res.data contains the BVN or similar
      }
      return null;
    } catch (error) {
      console.error("Error fetching BVN:", error);
      return null;
    }
  };

  const fetchGuarantorDetails = async (accountNumber, guarantorIndex) => {
    if (!accountNumber || accountNumber.length !== 10) {
      return;
    }
    showLoader();
    try {
      const token =
        from === "admin"
          ? localStorage.getItem("token")
          : localStorage.getItem("loanAdminToken");
      const res = await httpGet(`${customBaseUrl.loanUrl}/api/v1/admin/users/account/${accountNumber}`,
        null,
        false,
        false,
        token
      );
      if (res.status && res.data) {
        const { firstName, lastName, email, phoneNumber, tierDisplayName, tierOrderLevel } = res.data;
        const currentGuarantors = form.getFieldValue('guarantor_details');
        const fullName = `${firstName} ${lastName}`;
        const level = tierOrderLevel || 0;

        currentGuarantors[guarantorIndex] = {
          ...currentGuarantors[guarantorIndex],
          name: fullName,
          email: email,
          phone: phoneNumber,
          tier_display_name: tierDisplayName,
        };
        form.setFieldsValue({ guarantor_details: currentGuarantors });

        const newStatuses = [...guarantorStatuses];
        if (level < 3) {
          newStatuses[guarantorIndex] = false;
          NotificationManager.error(`Guarantor ${fullName} must have KYC Level 3`);
        } else {
          newStatuses[guarantorIndex] = true;
          NotificationManager.success("Guarantor details fetched successfully!");
        }
        setGuarantorStatuses(newStatuses);
      } else {
        NotificationManager.error(res.message || "Failed to fetch guarantor details.");
        const currentGuarantors = form.getFieldValue('guarantor_details');
        currentGuarantors[guarantorIndex] = {
          ...currentGuarantors[guarantorIndex],
          name: '',
          email: '',
          phone: '',
          tier_display_name: '',
        };
        form.setFieldsValue({ guarantor_details: currentGuarantors });

        const newStatuses = [...guarantorStatuses];
        newStatuses[guarantorIndex] = true; // Neutral state
        setGuarantorStatuses(newStatuses);
      }
    } catch (error) {
      NotificationManager.error("Error fetching guarantor details.");
      console.error("Error fetching guarantor details:", error);
    } finally {
      hideLoader();
    }
  };

  const fetchWayaRepaymentAccountDetails = async (accountNumber) => {
    if (!accountNumber || accountNumber.length !== 10) {
      return;
    }
    showLoader();
    try {
      const token =
        from === "admin"
          ? localStorage.getItem("token")
          : localStorage.getItem("loanAdminToken");

      const [accountRes, bvnRes] = await Promise.all([
        httpGet(`${customBaseUrl.loanUrl}/api/v1/admin/users/account/${accountNumber}`, null, false, false, token),
        httpGet(`${customBaseUrl.loanUrl}/api/v1/loans/waya/bvn-lookup?accountNumber=${accountNumber}`, null, false, false, token)
      ]);

      if (accountRes.status && accountRes.data) {
        setRepaymentAccountDetails(accountRes.data);
        const level = accountRes.data.tierOrderLevel || 0;
        setTierOrderLevel(level);

        if (level < 3) {
          setIsTierBlocked(true);
          NotificationManager.error(`Tier level too low (${accountRes.data.tierDisplayName}). A minimum of Tier 3 is required.`);
        } else {
          setIsTierBlocked(false);
          NotificationManager.success("Repayment account details fetched successfully!");
        }
      } else {
        setRepaymentAccountDetails(null);
        setTierOrderLevel(0);
        setIsTierBlocked(false);
        NotificationManager.error(accountRes.message || "Failed to fetch repayment account details.");
      }

      if (bvnRes.status) {
        setFetchedBvn(bvnRes.bvn);
        form.setFieldsValue({ bvn: bvnRes.bvn });
      }
    } catch (error) {
      setRepaymentAccountDetails(null);
      NotificationManager.error("Error fetching repayment account details.");
      console.error("Error fetching repayment account details:", error);
    } finally {
      hideLoader();
    }
  };

  const Layout = from === "admin" ? AdminLayout : LoanDashboardLayout;

  return (
    <Layout>
      <div style={styles.container}>
        <style>
          {`
            .ant-card-head {
              border-bottom: 1px solid #f0f0f0;
              padding: 0 24px;
            }
            .ant-card-head-title {
              font-weight: 700 !important;
              color: #333 !important;
            }
            .ant-form-item-label > label {
              font-weight: 600 !important;
              color: #555 !important;
            }
            .ant-input, .ant-input-number, .ant-select-selector {
              border-radius: 8px !important;
            }
            .ant-btn-primary:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(231, 71, 44, 0.4) !important;
            }
          `}
        </style>
        <Card
          title={<div style={styles.title}>Apply for Loan on Behalf of User</div>}
          style={styles.card}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              phone: repaymentAccountDetails?.phoneNumber,
              account_details: [{}],
              guarantor_details: [{}]
            }}
            onValuesChange={(changedValues, allValues) => {
              // Sync repayment details with the first entry in account_details
              if (allValues.account_details && allValues.account_details.length > 0) {
                const firstAccount = allValues.account_details[0];
                if (firstAccount) {
                  form.setFieldsValue({
                    repayment_details: {
                      account_name: firstAccount.account_name,
                      account_number: firstAccount.account_number,
                      account_type: firstAccount.account_type,
                      institution: firstAccount.institution,
                    }
                  });
                }
              }

              if (changedValues.hasOwnProperty('waya_repayment_account')) {
                const accNo = changedValues.waya_repayment_account;
                if (accNo && accNo.length === 10) {
                  fetchWayaRepaymentAccountDetails(accNo);
                } else {
                  // Initialize/Clear if deleted or less than 10 digits
                  setRepaymentAccountDetails(null);
                  setTierOrderLevel(0);
                  setIsTierBlocked(false);
                  setFetchedBvn("");
                  form.setFieldsValue({ bvn: "" });
                }
              }

              if (changedValues.guarantor_details) {
                const currentGuarantors = form.getFieldValue('guarantor_details');
                changedValues.guarantor_details.forEach((guarantor, index) => {
                  if (guarantor && guarantor.account_number) {
                    if (guarantor.account_number.length === 10) {
                      fetchGuarantorDetails(guarantor.account_number, index);
                    } else {
                      // Clear details if shortened
                      currentGuarantors[index] = {
                        ...currentGuarantors[index],
                        name: '',
                        email: '',
                        phone: '',
                        tier_display_name: '',
                      };
                      form.setFieldsValue({ guarantor_details: currentGuarantors });
                    }
                  }
                });
              }
            }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="type" label="Loan Type" rules={[{ required: true }]}>
                  <Select placeholder="Select Loan Type">
                    <Option value="POF">POF</Option>
                    <Option value="Personal">Personal</Option>
                    <Option value="Business">Business</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="waya_repayment_account"
                  label="Waya Primary Account"
                  rules={[
                    { required: true },
                    { len: 10, message: "Account number must be 10 digits" }
                  ]}
                >
                  <Input placeholder="Enter 10-digit Account Number" maxLength={10} />
                </Form.Item>
              </Col>
            </Row>

            {repaymentAccountDetails && (
              <Row gutter={16} style={{ marginBottom: '24px' }}>
                <Col span={24}>
                  <div style={styles.fetchStatus}>
                    <Descriptions
                      bordered
                      size="small"
                      layout="vertical"
                      column={{ xxl: 4, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
                    >
                      <Descriptions.Item label="Account Name"><strong>{repaymentAccountDetails.fullName}</strong></Descriptions.Item>
                      <Descriptions.Item label="Email">{repaymentAccountDetails.email}</Descriptions.Item>
                      <Descriptions.Item label="Phone">{repaymentAccountDetails.phoneNumber}</Descriptions.Item>
                      <Descriptions.Item label="BVN"><strong>{fetchedBvn || repaymentAccountDetails.bvn}</strong></Descriptions.Item>
                      <Descriptions.Item label="Tier Level">
                        <span style={{ color: tierOrderLevel >= 3 ? '#52c41a' : '#f5222d', fontWeight: 600 }}>
                          {repaymentAccountDetails.tierDisplayName || 'Unknown'}
                        </span>
                      </Descriptions.Item>
                    </Descriptions>
                  </div>
                </Col>
              </Row>
            )}

            <div style={{ height: '32px' }} />

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="amount"
                  label="Loan Amount"
                  rules={[{ required: true }]}
                >
                  <InputNumber
                    style={{ width: "100%", ...styles.input }}
                    formatter={value => `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\₦\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="term_months"
                  label="Term (Months)"
                  rules={[{ required: true }]}
                >
                  <InputNumber style={{ width: "100%", ...styles.input }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="bvn" label="BVN" hidden>
                  <Input disabled />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="needed_by"
                  label="Needed By"
                  rules={[{ required: true }]}
                >
                  <DatePicker style={{ width: "100%", ...styles.input }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="loan_start_date"
                  label="Loan Start Date"
                  rules={[{ required: true }]}
                >
                  <DatePicker style={{ width: "100%", ...styles.input }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="waya_repayment_account"
                  label="Waya Repayment Account"
                  rules={[{ required: true }]}
                >
                  <Input disabled style={styles.input} />
                </Form.Item>
              </Col>
            </Row>

            <Card title={<span style={styles.sectionTitle}>Other Data</span>} style={styles.secondaryCard}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name={["other_data", "monthly_income"]}
                    label="Monthly Income"
                  >
                    <InputNumber
                      style={{ width: "100%", ...styles.input }}
                      formatter={value => `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/\₦\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title={<span style={styles.sectionTitle}>Employment Details</span>} style={styles.secondaryCard}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name={["employment_details", "employer_name"]}
                    label="Employer Name"
                  >
                    <Input style={styles.input} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["employment_details", "salary"]}
                    label="Salary"
                  >
                    <InputNumber
                      style={{ width: "100%", ...styles.input }}
                      formatter={value => `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/\₦\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["employment_details", "occupation_type"]}
                    label="Occupation Type"
                  >
                    <Input style={styles.input} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name={["employment_details", "company_name"]}
                    label="Company Name"
                  >
                    <Input style={styles.input} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["employment_details", "company_address"]}
                    label="Company Address"
                  >
                    <Input style={styles.input} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["employment_details", "employment_date"]}
                    label="Employment Date"
                  >
                    <DatePicker style={{ width: "100%", ...styles.input }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["employment_details", "self_employed"]}
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Checkbox>Are you self-employed?</Checkbox>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.employment_details?.self_employed !== currentValues.employment_details?.self_employed}>
                {({ getFieldValue }) => {
                  const isSelfEmployed = getFieldValue(['employment_details', 'self_employed']);
                  if (!isSelfEmployed) {
                    return (
                      <Row gutter={16} style={{ marginTop: '16px' }}>
                        <Col span={8}>
                          <Form.Item
                            name={["employment_details", "hr_name"]}
                            label="HR Contact Name"
                            rules={[{ required: true, message: 'Please enter HR name' }]}
                          >
                            <Input style={styles.input} placeholder="Enter HR Name" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            name={["employment_details", "hr_email"]}
                            label="HR Contact Email"
                            rules={[{ required: true, type: 'email', message: 'Please enter a valid HR email' }]}
                          >
                            <Input style={styles.input} placeholder="Enter HR Email" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            name={["employment_details", "hr_phone"]}
                            label="HR Contact Phone"
                            rules={[{ required: true, message: 'Please enter HR phone number' }]}
                          >
                            <Input style={styles.input} placeholder="Enter HR Phone" />
                          </Form.Item>
                        </Col>
                      </Row>
                    );
                  }
                  return null;
                }}
              </Form.Item>
            </Card>

            <Card title={<span style={styles.sectionTitle}>File Uploads</span>} style={styles.secondaryCard}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="accountStatementFile"
                    label="Upload Account Statement"
                    rules={[{ required: true, message: 'Please upload account statement!' }]}
                  >
                    <Input type="file" onChange={(e) => setStatementOfAccountFile(e.target.files[0])} />
                  </Form.Item>
                  {uploadedStatementOfAccountUrl && (
                    <Alert
                      message="File Uploaded"
                      description={<a href={uploadedStatementOfAccountUrl} target="_blank" rel="noopener noreferrer">{uploadedStatementOfAccountUrl}</a>}
                      type="success"
                      showIcon
                      closable
                    />
                  )}
                </Col>
              </Row>
            </Card>

            <Form.List name="guarantor_details">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      title={<span style={{ fontWeight: 600 }}>Guarantor {name + 1}</span>}
                      extra={
                        name !== 0 && (
                          <Button onClick={() => remove(name)} danger size="small">
                            Remove
                          </Button>
                        )
                      }
                      style={styles.secondaryCard}
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "account_number"]}
                            label="WAYA Account Number"
                            rules={[{ len: 10, message: "Account number must be 10 digits" }]}
                          >
                            <Input maxLength={10} placeholder="Enter 10-digit Account Number" style={styles.input} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "name"]}
                            label="Name"
                          >
                            <Input disabled style={styles.input} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "email"]}
                            label="Email"
                          >
                            <Input disabled style={styles.input} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "phone"]}
                            label="Phone"
                          >
                            <Input disabled style={styles.input} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "tier_display_name"]}
                            label="Tier Level"
                          >
                            <Input disabled style={{ ...styles.input, color: color.primary, fontWeight: 600 }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block>
                      Add Guarantor
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.List
              name="account_details"
              rules={[
                {
                  validator: async (_, names) => {
                    if (!names || names.length < 1) {
                      return Promise.reject(new Error("At least one account detail is required"));
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      title={<span style={{ fontWeight: 600 }}>External Account Details {name + 1}</span>}
                      extra={
                        name !== 0 && (
                          <Button onClick={() => remove(name)} danger size="small">
                            Remove
                          </Button>
                        )
                      }
                      style={styles.secondaryCard}
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "institution", "bank_code"]} // Corrected name
                            label="Bank Name"
                          >
                            <Select
                              showSearch
                              style={styles.input}
                              placeholder="Select Bank"
                              onChange={(value, option) => {
                                const selectedBank = banks.find(
                                  (bank) => (bank.code ?? bank.nip_code) === value
                                );
                                const currentAccountDetails = form.getFieldValue("account_details");
                                currentAccountDetails[name] = {
                                  ...currentAccountDetails[name],
                                  institution: {
                                    ...currentAccountDetails[name]?.institution,
                                    name: selectedBank ? selectedBank.name : null,
                                    bank_code: value, // Set bank_code directly
                                  },
                                };
                                form.setFieldsValue({
                                  account_details: currentAccountDetails,
                                });
                              }}
                            >
                              {banks.map((bank) => (
                                <Option key={bank.code ?? bank.nip_code} value={bank.code ?? bank.nip_code}>
                                  {bank.name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "account_number"]}
                            label="Account Number"
                            rules={[{ len: 10, message: "Account number must be 10 digits" }]}
                          >
                            <Input maxLength={10} style={styles.input} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "account_name"]}
                            label="Account Name"
                          >
                            <Input style={styles.input} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, "account_type"]}
                            label="Account Type"
                          >
                            <Select style={styles.input}>
                              <Option value="SAVINGS">Savings</Option>
                              <Option value="CURRENT">Current</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Form.Item
                          {...restField}
                          hidden
                          name={[name, "institution", "name"]}
                        >
                          <Input />
                        </Form.Item>
                      </Row>
                    </Card>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block>
                      Add External Account Detail
                    </Button>
                    <Form.ErrorList />
                  </Form.Item>
                </>
              )}
            </Form.List>

            {/* Hidden Repayment Details section - synced automatically */}
            <div style={{ display: 'none' }}>
              <Card title="Repayment Details" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name={["repayment_details", "account_name"]}
                      label="Account Name"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name={["repayment_details", "account_number"]}
                      label="Account Number"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name={["repayment_details", "account_type"]}
                      label="Account Type"
                    >
                      <Select disabled>
                        <Option value="SAVINGS">Savings</Option>
                        <Option value="CURRENT">Current</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name={["repayment_details", "institution", "bank_code"]} // Corrected name
                      label="Bank Name"
                    >
                      <Select
                        disabled
                        showSearch
                        onChange={(value, option) => {
                          const selectedBank = banks.find(
                            (bank) => (bank.code ?? bank.nip_code) === value
                          );
                          form.setFieldsValue({
                            repayment_details: {
                              ...form.getFieldValue("repayment_details"),
                              institution: {
                                ...form.getFieldValue("repayment_details").institution,
                                name: selectedBank ? selectedBank.name : null,
                                bank_code: value, // Set bank_code directly
                              },
                            },
                          });
                        }}
                      >
                        {banks.map((bank) => (
                          <Option key={bank.code ?? bank.nip_code} value={bank.code ?? bank.nip_code}>
                            {bank.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Form.Item
                    hidden
                    name={["repayment_details", "institution", "name"]}
                  >
                    <Input />
                  </Form.Item>
                </Row>
              </Card>
            </div>

            <Card title={<span style={styles.sectionTitle}>Signature</span>} style={styles.secondaryCard}>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="signatureFiles"
                    label="Signature (Upload Image)"
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    rules={[{ required: true, message: 'Please upload a signature image!' }]}
                  >
                    <Upload
                      listType="picture-card"
                      className="signature-upload"
                      fileList={fileList}
                      onRemove={() => {
                        setFileList([]);
                        form.setFieldsValue({ signature: undefined });
                      }}
                      beforeUpload={beforeUpload}
                      onChange={handleChange}
                      maxCount={1}
                    >
                      {fileList?.length < 1 && (
                        <div>
                          <div style={{ color: color.primary, fontSize: '24px' }}>+</div>
                          <div style={{ marginTop: 8 }}>Upload Signature</div>
                          <div style={{ fontSize: '10px', color: '#999' }}>Max 100KB</div>
                        </div>
                      )}
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Form.Item style={{ textAlign: 'center' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  ...styles.submitBtn,
                  opacity: isTierBlocked ? 0.5 : 1,
                  cursor: isTierBlocked ? 'not-allowed' : 'pointer',
                  background: isTierBlocked ? '#d9d9d9' : styles.submitBtn.background
                }}
                block
                disabled={isTierBlocked || guarantorStatuses.some(status => status === false)}
              >
                {isTierBlocked || guarantorStatuses.some(status => status === false)
                  ? "Submission Blocked (Insufficient KYC/Tier)"
                  : "Submit Loan Application"}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </Layout>
  );
};

export default ApplyForLoan;
